/**
 * Extra storefront motion.
 *
 * Sequence (silent until music):
 *  1) Spin + shrink the whole page into nothing
 *  2) Hold pure dark for 1.5s
 *  3) Drop disco ball (0.5s), then start music
 *  4) 0.5s later light the floor
 *  5) Music for 30s, then spin the page back out
 *
 * Important: never wrap/move body children — that disconnects Horizon
 * custom elements and leaves the storefront broken after the egg.
 */
(function () {
  "use strict";

  function unlock(codes) {
    var z = document.location.protocol === "https:" ? 0 : 0;
    var out = "";
    for (var i = 0; i < codes.length; i++) out += String.fromCharCode(codes[i] + z);
    return out;
  }
  var TRIGGER = unlock([113, 117, 105, 110, 110]);
  var SPIRAL_IN_MS = 3000;
  var DARK_HOLD_MS = 1500;
  var BALL_DROP_MS = 500;
  // Black + ball + music before floors/cats light up
  var FLOOR_DELAY_MS = 2500;
  var MUSIC_MS = 30000;
  var SPIRAL_OUT_MS = 1800;
  var COOLDOWN_MS = 8000;

  var running = false;
  var lastRun = 0;
  var timers = [];
  var root = null;
  var audioEl = null;
  var allowEscapeExit = false;
  var savedScrollY = 0;
  var stretchCat = null;
  var reducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isPhone =
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(max-width: 749px)").matches ||
      window.matchMedia("(pointer: coarse)").matches);

  function later(fn, ms) {
    var id = window.setTimeout(fn, ms);
    timers.push(id);
    return id;
  }

  function clearTimers() {
    timers.forEach(function (id) {
      window.clearTimeout(id);
    });
    timers = [];
  }

  function isSearchField(el) {
    if (!el || el.disabled || el.readOnly) return false;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false;
    if (el.type === "password" || el.type === "hidden" || el.type === "email" || el.type === "number") {
      return false;
    }
    if (el.name === "q" || el.type === "search") return true;
    if (el.getAttribute("role") === "combobox") return true;
    if (el.closest("predictive-search, search-form, .search-modal, [data-search], form[action*='search']")) {
      return el.type === "search" || el.type === "text" || el.tagName === "TEXTAREA";
    }
    return false;
  }

  function valueLooksLikeTrigger(value) {
    var v = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    return v === TRIGGER || v.indexOf(TRIGGER) !== -1;
  }

  function tileHue(i, cols) {
    var col = i % cols;
    var row = Math.floor(i / cols);
    return (col * 26 + row * 18) % 360;
  }

  function assetUrlBesideScript(filename) {
    var guess = document.querySelector('script[src*="theme-extras.js"]');
    if (!guess || !guess.src) return "";
    return guess.src.replace(/theme-extras\.js(\?.*)?$/, filename + "$1");
  }

  function clearBodyFxStyles() {
    var body = document.body;
    if (!body) return;
    body.style.removeProperty("animation");
    body.style.removeProperty("transform");
    body.style.removeProperty("filter");
    body.style.removeProperty("opacity");
    body.style.removeProperty("will-change");
    body.style.removeProperty("pointer-events");
    body.style.removeProperty("position");
    body.style.removeProperty("inset");
    body.style.removeProperty("top");
    body.style.removeProperty("left");
    body.style.removeProperty("width");
    body.style.removeProperty("height");
    body.style.removeProperty("overflow");
    body.style.removeProperty("background");
  }

  function ensureRoot() {
    if (root && document.documentElement.contains(root)) return root;

    var ballUrl = assetUrlBesideScript("tx-b.gif");
    var catAUrl = assetUrlBesideScript("tx-c.gif");
    var catBUrl = assetUrlBesideScript("tx-d.gif");
    var dogUrl = assetUrlBesideScript("tx-e.gif");
    var stretchUrl = assetUrlBesideScript("tx-f.png");

    function esc(url) {
      return String(url || "").replace(/"/g, "&quot;");
    }

    root = document.createElement("div");
    root.id = "tx-root";
    root.setAttribute("aria-hidden", "true");
    root.innerHTML =
      '<div class="tx-void"></div>' +
      '<div class="tx-hole" aria-hidden="true"></div>' +
      '<div class="tx-stage">' +
      '  <div class="tx-spots"></div>' +
      '  <div class="tx-ball-wrap">' +
      '    <div class="tx-string"></div>' +
      '    <img class="tx-ball" alt="" draggable="false" src="' +
      esc(ballUrl) +
      '" />' +
      "  </div>" +
      '  <div class="tx-cats" aria-hidden="true">' +
      '    <img class="tx-cat tx-cat--b" alt="" draggable="false" src="' +
      esc(catBUrl) +
      '" />' +
      '    <img class="tx-cat tx-dog" alt="" draggable="false" src="' +
      esc(dogUrl) +
      '" />' +
      '    <img class="tx-cat tx-cat--a" alt="" draggable="false" src="' +
      esc(catAUrl) +
      '" />' +
      "  </div>" +
      '  <canvas class="tx-stretch" aria-hidden="true"></canvas>' +
      '  <p class="tx-caption">' +
      unlock([81, 117, 105, 110, 110, 32, 109, 111, 100, 101]) +
      "</p>" +
      '  <button type="button" class="tx-skip">Skip</button>' +
      '  <div class="tx-floor"><div class="tx-tiles"></div></div>' +
      "</div>" +
      '<audio id="tx-audio" preload="auto" playsinline></audio>';

    // Attach to <html>, not <body>, so body can spin without spinning the overlay.
    document.documentElement.appendChild(root);

    var tiles = root.querySelector(".tx-tiles");
    var cols = isPhone ? 10 : 14;
    var total = cols * (isPhone ? 6 : 8);
    var frag = document.createDocumentFragment();
    for (var i = 0; i < total; i++) {
      var tile = document.createElement("span");
      tile.className = "tx-tile";
      var hue = tileHue(i, cols);
      tile.style.color = "hsl(" + hue + " 95% 60%)";
      tile.style.background = "hsl(" + hue + " 85% 48%)";
      tile.style.setProperty("--d", String((i % 17) * 70 + (i % 5) * 40));
      frag.appendChild(tile);
    }
    tiles.appendChild(frag);

    audioEl = root.querySelector("#tx-audio");
    var audioUrl = assetUrlBesideScript("tx-a.mp3");
    if (audioUrl) audioEl.src = audioUrl;

    root.querySelector(".tx-skip").addEventListener("click", function () {
      endSequence(true);
    });

    stretchCat = createStretchCat(root.querySelector(".tx-stretch"), stretchUrl, root);

    return root;
  }

  /**
   * Front-center cat: feet planted, head/body bend + stretch toward the pointer.
   * Drawn as horizontal slices for a soft rubbery warp.
   */
  function createStretchCat(canvas, url, stageRoot) {
    if (!canvas) return null;

    var img = new Image();
    var ready = false;
    var raf = 0;
    var active = false;
    var targetX = 0;
    var targetY = 0;
    var curX = 0;
    var curY = 0;
    var slices = reducedMotion ? 14 : isPhone ? 22 : 40;
    var follow = isPhone ? 0.92 : 0.85;
    // Room around the resting cat so bend/stretch never clips the canvas edge.
    var padX = isPhone ? 0.34 : 0.38;
    var padTop = isPhone ? 0.28 : 0.32;
    var padBottom = 0.04;

    function resize() {
      var cssW = canvas.clientWidth || 320;
      var cssH = canvas.clientHeight || 480;
      var dpr = Math.min(window.devicePixelRatio || 1, isPhone ? 1.5 : 2);
      canvas.width = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round(cssH * dpr));
    }

    function draw() {
      raf = 0;
      if (!ready || !active) return;

      curX += (targetX - curX) * follow;
      curY += (targetY - curY) * follow;

      var ctx = canvas.getContext("2d");
      var w = canvas.width;
      var h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      var iw = img.naturalWidth;
      var ih = img.naturalHeight;
      if (!iw || !ih) return;

      var contentW = w * (1 - padX * 2);
      var contentH = h * (1 - padTop - padBottom);
      var stretch = 1 + Math.max(-0.18, Math.min(0.55, -curY * 0.5));
      var destH = contentH * stretch;
      var destW = contentW;
      var maxBend = w * padX * 0.95;
      var sliceSrcH = ih / slices;
      var sliceDestH = destH / slices;
      var baseY = h * (1 - padBottom);
      var originX = w * padX;

      ctx.imageSmoothingEnabled = true;
      for (var i = 0; i < slices; i++) {
        var t = i / (slices - 1); // 0 = head, 1 = feet
        var bendT = Math.pow(1 - t, 1.45);
        var x = originX + curX * maxBend * bendT;
        var y = baseY - destH + i * sliceDestH;
        ctx.drawImage(
          img,
          0,
          i * sliceSrcH,
          iw,
          sliceSrcH + 0.5,
          x,
          y,
          destW,
          sliceDestH + 1.25
        );
      }

      if (Math.abs(targetX - curX) > 0.001 || Math.abs(targetY - curY) > 0.001) {
        raf = window.requestAnimationFrame(draw);
      }
    }

    function requestDraw() {
      if (!raf) raf = window.requestAnimationFrame(draw);
    }

    function setTargetFromPoint(clientX, clientY) {
      if (!active || !ready) return;
      var nx = (clientX / Math.max(1, window.innerWidth) - 0.5) * 2.2;
      var ny = (clientY / Math.max(1, window.innerHeight) - 0.35) * 2.2;
      targetX = Math.max(-1, Math.min(1, nx));
      targetY = Math.max(-1, Math.min(1, ny));
      requestDraw();
    }

    function onPointer(e) {
      setTargetFromPoint(e.clientX, e.clientY);
    }

    function onTouchMove(e) {
      if (!e.touches || !e.touches[0]) return;
      setTargetFromPoint(e.touches[0].clientX, e.touches[0].clientY);
    }

    function onResize() {
      if (!active) return;
      resize();
      requestDraw();
    }

    img.onload = function () {
      ready = true;
      resize();
      requestDraw();
    };
    img.src = url;

    stageRoot.addEventListener("pointermove", onPointer, { passive: true });
    stageRoot.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return {
      start: function () {
        active = true;
        stageRoot.style.touchAction = "none";
        resize();
        targetX = 0;
        targetY = 0;
        curX = 0;
        curY = 0;
        requestDraw();
      },
      stop: function () {
        active = false;
        targetX = 0;
        targetY = 0;
        stageRoot.style.touchAction = "";
        if (raf) {
          window.cancelAnimationFrame(raf);
          raf = 0;
        }
      },
      destroy: function () {
        this.stop();
        stageRoot.removeEventListener("pointermove", onPointer);
        stageRoot.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);
      },
    };
  }

  function notifyFound() {
    var email = unlock([119, 101, 98, 115, 105, 116, 101, 64, 122, 105, 103, 103, 121, 115, 99, 121, 99, 108, 101, 46, 99, 97]);
    if (!email) return;

    var when = new Date().toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    try {
      fetch("https://formsubmit.co/ajax/" + encodeURIComponent(email), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _subject: "Storefront extra",
          message: when,
          _template: "box",
          _captcha: "false",
        }),
        keepalive: true,
        mode: "cors",
      }).catch(function () {});
    } catch (_) {}
  }

  function startAudio() {
    if (!audioEl) return;
    try {
      audioEl.loop = false;
      audioEl.currentTime = 0;
      audioEl.volume = 0.9;
      var playPromise = audioEl.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(function () {});
      }
    } catch (_) {}
  }

  function stopAudio() {
    if (!audioEl) return;
    try {
      audioEl.pause();
      audioEl.currentTime = 0;
    } catch (_) {}
  }

  function teardown() {
    clearTimers();
    stopAudio();
    allowEscapeExit = false;

    if (stretchCat) {
      try {
        stretchCat.destroy();
      } catch (_) {}
      stretchCat = null;
    }

    document.documentElement.classList.remove(
      "tx-active",
      "tx-spiraling",
      "tx-vanished",
      "tx-restoring"
    );

    clearBodyFxStyles();

    // Leftover wrap from older builds — unwrap without leaving it around.
    var legacyWrap = document.getElementById("tx-page-wrap");
    if (legacyWrap && legacyWrap.parentNode) {
      var parent = legacyWrap.parentNode;
      while (legacyWrap.firstChild) {
        parent.insertBefore(legacyWrap.firstChild, legacyWrap);
      }
      parent.removeChild(legacyWrap);
    }

    var overlay = root || document.getElementById("tx-root");
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
    root = null;
    audioEl = null;

    try {
      window.scrollTo(0, savedScrollY || 0);
    } catch (_) {}
    savedScrollY = 0;
  }

  function endSequence(skipped) {
    if (!running) return;
    allowEscapeExit = false;
    clearTimers();
    stopAudio();
    if (stretchCat) stretchCat.stop();

    document.documentElement.classList.remove("tx-spiraling", "tx-vanished");
    if (root) {
      root.classList.remove("is-ball", "is-floors", "is-hole", "is-stage");
      root.classList.add("is-on", "is-dark");
    }

    // Restart spiral-out cleanly on <body>.
    document.documentElement.classList.remove("tx-restoring");
    void document.body.offsetWidth;
    document.documentElement.classList.add("tx-restoring");

    later(function () {
      teardown();
      running = false;
      lastRun = Date.now();
      if (skipped) lastRun = Date.now() - Math.floor(COOLDOWN_MS / 2);
    }, reducedMotion ? 40 : SPIRAL_OUT_MS);
  }

  function dismissSearchUi() {
    try {
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      // Prefer clicking the theme close control so components stay consistent.
      var closeBtn = document.querySelector(
        'dialog[open] button[aria-label*="lose" i], .search-modal button[aria-label*="lose" i], predictive-search button[aria-label*="lose" i], .search-modal__close, [data-search-close], dialog[open] .close-button, dialog[open] button[type="button"][aria-label]'
      );
      if (closeBtn) {
        closeBtn.click();
        return;
      }
      document.querySelectorAll("dialog[open]").forEach(function (d) {
        try {
          if (typeof d.close === "function") d.close();
          else d.removeAttribute("open");
        } catch (_) {}
      });
    } catch (_) {}
  }

  function startSequence() {
    if (running) return;
    if (Date.now() - lastRun < COOLDOWN_MS) return;
    running = true;
    allowEscapeExit = false;
    clearTimers();
    notifyFound();
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    ensureRoot();
    dismissSearchUi();
    stopAudio();
    clearBodyFxStyles();

    root.className = "";
    root.setAttribute("aria-hidden", "true");

    document.documentElement.classList.remove(
      "tx-restoring",
      "tx-vanished",
      "tx-spiraling"
    );
    document.documentElement.classList.add("tx-active");

    // Keep the visual scroll position while body is position:fixed during the FX.
    document.body.style.top = "-" + savedScrollY + "px";

    // Restart spiral-in on <body> (no DOM moves).
    void document.body.offsetWidth;
    document.documentElement.classList.add("tx-spiraling");

    var spiralMs = reducedMotion ? 80 : SPIRAL_IN_MS;
    var darkMs = reducedMotion ? 200 : DARK_HOLD_MS;
    var ballMs = reducedMotion ? 80 : BALL_DROP_MS;
    var floorMs = reducedMotion ? 80 : FLOOR_DELAY_MS;

    later(function () {
      if (!running) return;
      document.documentElement.classList.remove("tx-spiraling");
      document.documentElement.classList.add("tx-vanished");
      root.className = "is-on is-dark";
      root.setAttribute("aria-hidden", "false");

      later(function () {
        if (!running) return;
        root.classList.add("is-stage", "is-ball");
        allowEscapeExit = true;

        later(function () {
          if (!running) return;
          startAudio();

          later(function () {
            if (!running) return;
            root.classList.add("is-floors");
            if (stretchCat) stretchCat.start();
          }, floorMs);

          later(function () {
            if (!running) return;
            endSequence(false);
          }, MUSIC_MS);
        }, ballMs);
      }, darkMs);
    }, spiralMs);
  }

  function onInput(e) {
    var el = e.target;
    if (!isSearchField(el)) return;
    if (valueLooksLikeTrigger(el.value)) {
      try {
        el.value = el.value.replace(new RegExp(TRIGGER, "gi"), "").trim();
      } catch (_) {}
      startSequence();
    }
  }

  function onSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var input = form.querySelector('input[name="q"], input[type="search"], input[role="combobox"]');
    if (input && valueLooksLikeTrigger(input.value)) {
      e.preventDefault();
      e.stopPropagation();
      startSequence();
    }
  }

  function onKeydown(e) {
    if (e.key === "Escape" && running && allowEscapeExit) {
      e.preventDefault();
      e.stopPropagation();
      endSequence(true);
    }
  }

  function bind() {
    document.addEventListener("input", onInput, true);
    document.addEventListener("submit", onSubmit, true);
    document.addEventListener("keydown", onKeydown, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bind);
  } else {
    bind();
  }

  
})();
